import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth, useModuleVisibility } from "../App";
import { usePanels } from "../hooks/usePanels";
import { ITEMS, PANEL_ICON_MAP, PANEL_COLORS } from "../config/panels";
import { ChevronRight, Crown, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_LABELS = {
  free: "Starter",
  starter: "Starter",
  pro: "Pro",
  business: "Business",
  enterprise: "Enterprise",
};

const PanelCard = ({ panel, visibleItems, onOpen }) => {
  const Icon = PANEL_ICON_MAP[panel.icon] || PANEL_ICON_MAP.LayoutDashboard;
  const colour = PANEL_COLORS[panel.color] || PANEL_COLORS.slate;
  const itemCount = visibleItems.length;

  return (
    <button
      type="button"
      onClick={() => onOpen(panel, visibleItems)}
      disabled={itemCount === 0}
      data-testid={`panel-card-${panel.id}`}
      className={`group relative text-left rounded-2xl border border-slate-700/60 bg-gradient-to-br ${colour.grad}
                  bg-slate-900/40 backdrop-blur-sm p-6 transition-all duration-300
                  hover:border-slate-500/80 hover:shadow-2xl hover:-translate-y-1
                  focus:outline-none focus:ring-2 ${colour.ring}
                  disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0`}
    >
      <div className="flex items-start justify-between mb-5">
        <div className={`p-3 rounded-xl bg-slate-800/60 ${colour.icon} ring-1 ring-inset ring-slate-700/50`}>
          <Icon size={28} strokeWidth={1.75} />
        </div>
        <span className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-medium text-slate-300`}>
          <span className={`w-1.5 h-1.5 rounded-full ${colour.dot}`} />
          {itemCount} {itemCount === 1 ? "Modul" : "Module"}
        </span>
      </div>

      <h3 className="text-lg font-semibold text-white mb-1.5 tracking-tight">{panel.label}</h3>
      <p className="text-sm text-slate-400 leading-relaxed min-h-[40px]">{panel.description}</p>

      <div className="mt-4 pt-4 border-t border-slate-700/40">
        <div className="flex flex-wrap gap-1.5">
          {visibleItems.slice(0, 4).map((item) => (
            <span
              key={item.path}
              className="text-[10.5px] px-2 py-0.5 rounded-md bg-slate-800/70 text-slate-300 border border-slate-700/50"
            >
              {item.label}
            </span>
          ))}
          {visibleItems.length > 4 && (
            <span className="text-[10.5px] px-2 py-0.5 rounded-md text-slate-400">
              +{visibleItems.length - 4}
            </span>
          )}
        </div>
      </div>

      <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
        <ChevronRight size={18} className="text-slate-400" />
      </div>
    </button>
  );
};

export const PanelsDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { visibility } = useModuleVisibility();
  const { panels } = usePanels();

  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/subscriptions/current`, { withCredentials: true })
      .then((r) => setSubscription(r.data))
      .catch(() => setSubscription(null));
  }, []);

  const resolveItems = (panel) =>
    panel.items
      .map((id) => ITEMS[id])
      .filter(Boolean)
      .filter((item) => item.key === null || visibility[item.key]);

  const handleOpenPanel = (panel, visibleItems) => {
    const first = visibleItems[0];
    if (first) navigate(first.path.split("?")[0]);
  };

  const planId = subscription?.subscription?.plan || "free";
  const planLabel = PLAN_LABELS[planId] || "Starter";
  const firstName = user?.name?.split(" ")[0] || "";

  const now = new Date();
  const hour = now.getHours();
  const greeting = hour < 11 ? "Guten Morgen" : hour < 18 ? "Hallo" : "Guten Abend";

  return (
    <div className="min-h-screen" data-testid="panels-dashboard">
      <div className="px-6 md:px-10 pt-10 pb-6">
        <div className="flex flex-wrap items-start justify-between gap-6 mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-violet-400/80 font-medium mb-2">
              MargeVio Arbeitsbereich
            </p>
            <h1 className="text-4xl md:text-5xl font-semibold text-white tracking-tight">
              {greeting}
              {firstName ? `, ${firstName}` : ""}.
            </h1>
            <p className="mt-3 text-base text-slate-400 max-w-2xl">
              Wähle ein Panel um fortzufahren. Alles was du für den Betrieb brauchst — in klarer Struktur.
            </p>
          </div>

          <button
            type="button"
            onClick={() => navigate("/konto")}
            className="group inline-flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-900/70 border border-slate-700/60 hover:border-violet-500/40 transition-all"
            data-testid="konto-quick-access"
          >
            <div className="p-2 rounded-lg bg-violet-500/15 text-violet-300 ring-1 ring-inset ring-violet-500/30">
              <Crown size={18} />
            </div>
            <div className="text-left">
              <p className="text-[10.5px] uppercase tracking-wider text-slate-500">Abonnement</p>
              <p className="text-sm font-semibold text-white flex items-center gap-1.5">
                {planLabel}
                {planId === "free" && <Sparkles size={12} className="text-amber-400" />}
              </p>
            </div>
            <ChevronRight size={16} className="text-slate-500 group-hover:text-white transition-colors" />
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {panels.map((panel) => (
            <PanelCard
              key={panel.id}
              panel={panel}
              visibleItems={resolveItems(panel)}
              onOpen={handleOpenPanel}
            />
          ))}
        </div>

        <p className="mt-10 text-xs text-slate-500 text-center">
          Panel-Struktur anpassen in{" "}
          <button
            type="button"
            onClick={() => navigate("/einstellungen?tab=panels")}
            className="underline hover:text-violet-400 transition-colors"
            data-testid="customize-panels-link"
          >
            Einstellungen → Panels
          </button>
        </p>
      </div>
    </div>
  );
};

export default PanelsDashboard;
